import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  if (window.__mpSingleLicensePerBeatInit) return;
  window.__mpSingleLicensePerBeatInit = true;
  var nativeFetch = window.fetch.bind(window);

  // Business rule: a customer can only have ONE unit of a given beat+license
  // combination in the cart at a time (no "buy the same license for the same
  // beat twice" and no stacking quantity past 1 on one variant), and only one
  // license PER BEAT overall (picking a different license for a beat that's
  // already in the cart swaps it in, it doesn't add a second line).
  //
  // Two mechanisms enforce this together:
  //  1. BEFORE sending /cart/add.js, check whether the requested variant is
  //     already a line in the cart. If so, block the add entirely (return a
  //     synthetic Shopify-style error response so the theme's existing
  //     add-to-cart error UI shows a message) instead of letting Shopify's
  //     native merge silently bump that line's quantity to 2, 3, 4...
  //  2. AFTER a successful add, remove any other cart line for the same
  //     product (beat) but a DIFFERENT variant (license) - this is the
  //     "swap license" behavior and was already in place before this fix.

  var ALREADY_IN_CART_MESSAGE =
    "You already have this beat with this license in your cart. Remove it first if you'd like to change the license.";

  function isCartAddUrl(u) {
    if (!u) return false;
    var p = typeof u === 'string' ? u : u.url || '';
    return /\/cart\/add(\.js)?(\?|$)/.test(p);
  }

  function extractAddedItems(json) {
    var items = [];
    if (json && Array.isArray(json.items)) {
      json.items.forEach(function (it) {
        if (it && it.product_id != null && (it.variant_id != null || it.id != null)) {
          items.push({ variantId: String(it.variant_id != null ? it.variant_id : it.id), productId: it.product_id });
        }
      });
    } else if (json && json.product_id != null) {
      items.push({ variantId: String(json.variant_id != null ? json.variant_id : json.id), productId: json.product_id });
    }
    return items;
  }

  function getCartItemsSectionIds() {
    return Array.prototype.map
      .call(document.querySelectorAll('cart-items-component'), function (el) {
        return el.dataset.sectionId;
      })
      .filter(Boolean);
  }

  function removeLine(lineKey, sectionIds) {
    return nativeFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  function dispatchCartLinesUpdate(ajaxCart, removedLineKey) {
    var sectionIds = getCartItemsSectionIds();
    var itemCount = 0;
    var primarySectionId = sectionIds[0];
    if (primarySectionId && ajaxCart.sections && ajaxCart.sections[primarySectionId]) {
      try {
        var doc = new DOMParser().parseFromString(ajaxCart.sections[primarySectionId], 'text/html');
        var el = doc.querySelector('[ref="cartItemCount"]');
        if (el) itemCount = parseInt(el.textContent, 10) || 0;
      } catch (e) {}
    }

    var deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: removedLineKey, quantity: 0 }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        sections: ajaxCart.sections,
        items: ajaxCart.items,
        itemCount: itemCount,
        source: 'single-license-per-beat',
        didError: false,
      },
    });
  }

  function cleanupDuplicateLicenses(addedItems) {
    if (!addedItems.length) return;
    nativeFetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var toRemove = [];
        addedItems.forEach(function (added) {
          (cart.items || []).forEach(function (line) {
            if (String(line.product_id) === String(added.productId) && String(line.variant_id) !== added.variantId) {
              toRemove.push(line);
            }
          });
        });
        if (!toRemove.length) return;

        var sectionIds = getCartItemsSectionIds();
        var chain = Promise.resolve();
        var lastResponse = null;
        var lastKey = null;
        toRemove.forEach(function (line) {
          chain = chain.then(function () {
            return removeLine(line.key, sectionIds)
              .then(function (data) {
                lastResponse = data;
                lastKey = line.key;
              })
              .catch(function () {});
          });
        });
        chain.then(function () {
          if (lastResponse) dispatchCartLinesUpdate(lastResponse, lastKey);
        });
      })
      .catch(function () {});
  }

  // Pulls the variant id(s) an /cart/add.js request is trying to add, from
  // whichever request-body shape the theme happens to use for that call:
  // a FormData (the normal product-form / quick-add submit), a URLSearchParams,
  // or a JSON body (single {id, quantity} or a batch {items:[{id, quantity}]}).
  function parseRequestedVariantIds(init) {
    var body = init && init.body;
    if (!body) return null;

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var id = body.get('id');
      return id ? [String(id)] : null;
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      var uid = body.get('id');
      return uid ? [String(uid)] : null;
    }

    if (typeof body === 'string') {
      try {
        var parsed = JSON.parse(body);
        if (parsed && Array.isArray(parsed.items)) {
          var ids = parsed.items
            .map(function (it) {
              return it && it.id != null ? String(it.id) : null;
            })
            .filter(Boolean);
          return ids.length ? ids : null;
        }
        if (parsed && parsed.id != null) {
          return [String(parsed.id)];
        }
      } catch (e) {
        return null;
      }
    }

    return null;
  }

  function buildAlreadyInCartResponse() {
    return new Response(
      JSON.stringify({
        status: 422,
        message: ALREADY_IN_CART_MESSAGE,
        description: ALREADY_IN_CART_MESSAGE,
        errors: ALREADY_IN_CART_MESSAGE,
      }),
      { status: 200, headers: { 'Content-Type': 'application/json' } }
    );
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : input && input.url;

    function performAdd() {
      return nativeFetch(input, init).then(function (response) {
        if (response && response.ok) {
          response
            .clone()
            .json()
            .then(function (json) {
              if (json && json.status) return;
              var addedItems = extractAddedItems(json);
              if (addedItems.length) cleanupDuplicateLicenses(addedItems);
            })
            .catch(function () {});
        }
        return response;
      });
    }

    if (!isCartAddUrl(url) || !init) {
      return nativeFetch(input, init);
    }

    var requestedVariantIds = parseRequestedVariantIds(init);
    if (!requestedVariantIds || !requestedVariantIds.length) {
      return performAdd();
    }

    return nativeFetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var existingVariantIds = (cart.items || []).map(function (line) {
          return String(line.variant_id);
        });
        var alreadyPresent = requestedVariantIds.some(function (id) {
          return existingVariantIds.indexOf(id) !== -1;
        });

        if (alreadyPresent) {
          return buildAlreadyInCartResponse();
        }

        return performAdd();
      })
      .catch(function () {
        // If our own pre-check fails for any reason, fail open and let the
        // real add through rather than silently blocking a legitimate add.
        return performAdd();
      });
  };
})();
